// @flow

import React from 'react';
import { isPlainObject } from 'lodash';

import { decodeErrorBundle } from '@zapier/product-get-help';

import { base } from 'app/get-help/utils/tree';

import Error from './Error';
import { CUSTOM_KEY } from './constants';

import type { NodeDefinition } from 'app/get-help/utils/tree/types';

import { type NodeOptions } from './types';

const error = (options: NodeOptions): NodeDefinition => {
  const children = {
    [CUSTOM_KEY]: options.customChild,
  };
  const items = [];

  options.items.forEach(({ child, ...item }) => {
    if (item.value === CUSTOM_KEY) {
      throw new Error(
        `The value '${CUSTOM_KEY}' is reserved for a custom error passed to the form.`
      );
    }
    if (item.value in children) {
      throw new Error(
        `Each item should have an unique ID. Found dupe: ${item.value}.`
      );
    }

    items.push(item);
    children[item.value] = child;
  });

  const node = base({
    id: 'error',
    children,
    parseData: data => {
      // pre-filled
      if (data && typeof data === 'string') {
        // pre-filled with static item value
        if (node.children[data]) {
          return {
            value: data,
          };
        }

        // pre-filled with error
        const custom = decodeErrorBundle(data);
        if (custom) {
          return {
            custom,
            value: 'custom',
          };
        }
      } else if (isPlainObject(data)) {
        return data;
      }
      return {};
    },
    isDataValid: data => {
      return !!node.getNextNode(data, {});
    },
    getNextNode: data => {
      const { value } = isPlainObject(data) ? data : {}; // flow
      return typeof value === 'string' ? node.children[value] : null;
    },
    render: props => {
      return <Error {...props} items={items} label={options.label} />;
    },
    export: data => {
      const { value, custom } = isPlainObject(data) ? data : {}; // flow
      return {
        id: node.id,
        data,
        label: options.label,
        formatted:
          value === CUSTOM_KEY
            ? custom.title
            : (items.find(item => item.value === value) || {}).label,
      };
    },
  });

  return node;
};

export default error;
